import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DangerousIcon from "@mui/icons-material/Dangerous";
import SailingIcon from "@mui/icons-material/Sailing";
import { Chip, Stack, Typography } from "@mui/material";
import {
  PendingChip,
  RagChip,
} from "@poseidon/ui/components/RagChip";
import { formatCurrency } from "@poseidon/utils";
import { WithDoc } from "@poseidon/firebase/components/WithDoc";
import { camelToSentenceCase } from "@poseidon/utils/text";
import { LazyActionChip, PepStatusChip } from "../../../Action/ActionChip";
import { getUserLabel } from "../../../Users/util";
import {
  dateColumn,
  filterOperators,
  getActionColumnProperties,
} from "./../../../DataGrid/ColumnHelpers";

const kycAmlStatus = ({ data: { kycAmlStatus } }) => {
  if ("highRisk" === kycAmlStatus) {
    return <RagChip color="orange" label="High Risk" />;
  }

  if ("lowRisk" === kycAmlStatus) {
    return <RagChip color="green" label="Low Risk" />;
  }
  return <PendingChip />;
};

const creditUnderwriting = ({ data: { creditSummaryStatus } }) => {
  if ("pass" === creditSummaryStatus) {
    return <RagChip color="green" label="Approved" />;
  }
  if ("fail" === creditSummaryStatus) {
    return <RagChip color="red" label="Declined" />;
  }
  return <RagChip color="blue" label="Pending" />;
};

export const columnDefinitions = [
  {
    field: "id",
    headerName: "ID",
    width: 180,
    filterOperators,
  },
  {
    field: "applicationNumber",
    headerName: "Application Number",
    width: 100,
    filterOperators,
    hideable: false,
  },
  {
    field: "status",
    headerName: "Status",
    width: 140,
    filterOperators,
    renderCell: ({ value }) => {
      if (value === "active") {
        return (
          <Chip color="success" icon={<CheckCircleIcon />} label="Active" />
        );
      }
      if (value === "agreement") {
        return (
          <Chip color="primary" icon={<SailingIcon />} label="Agreement" />
        );
      }
      return (
        <Chip
          color="error"
          icon={<DangerousIcon />}
          label={camelToSentenceCase(value ?? "invalid")}
        />
      );
    },
  },
  dateColumn({ field: "createdDate", headerName: "Created Date" }),
  {
    field: "sailor",
    headerName: "Sailor(s)",
    description: "",
    width: 140,
    filterOperators,
    renderCell: ({ row }) => {
      if (!row.applicants?.length) {
        return;
      }
      const hasJointApplicant = row.applicants.length > 1;
      const applicantId = row.applicants.at(0)?.id;
      if (!applicantId) return;
      const jointApplicantId = hasJointApplicant
        ? row.applicants.at(1).id
        : false;
      return (
        <Stack>
          <WithDoc
            docRef={`sailors/${applicantId}`}
            Component={({ data: sailor }) => (
              <Typography variant="p">{getUserLabel(sailor)}</Typography>
            )}
          />
          {hasJointApplicant && (
            <WithDoc
              docRef={`sailors/${jointApplicantId}`}
              Component={({ data: sailor }) => (
                <Typography variant="p" color="textSecondary">
                  {getUserLabel(sailor)}
                </Typography>
              )}
            />
          )}
        </Stack>
      );
    },
  },
  {
    field: "principal",
    headerName: "Loan Amount",
    width: 100,
    valueGetter: ({ value }) => formatCurrency(value),
  },
  {
    field: "vessel",
    headerName: "Vessel",
    type: "string",
    width: 150,
    filterOperators,
    renderCell: ({ row }) => {
      if (!row.vessel) {
        return;
      }
      const boatId = row.vessel.id;
      return (
        <Stack>
          <WithDoc
            docRef={`boats/${boatId}`}
            Component={({ data: boat }) => (
              <>
                <Typography variant="p">
                  {boat.make ?? "Make Unknown"}: {boat.model ?? "Model Unknown"}
                </Typography>
                <Typography variant="caption">{boat.type}</Typography>
              </>
            )}
          />
        </Stack>
      );
    },
  },
  {
    field: "dealer",
    headerName: "Dealer",
    type: "string",
    width: 150,
    filterOperators,
    renderCell: ({ row }) => {
      if (!row.dealer) {
        return <RagChip label="No Dealer" />;
      }
      const dealerId = row.dealer.id;
      return (
        <Stack>
          <WithDoc
            docRef={`dealers/${dealerId}`}
            Component={({ data: dealer }) => dealer.companyName}
            fallbackValue={<RagChip label="No Dealer" />}
          />
        </Stack>
      );
    },
  },
  {
    field: "kycAmlStatus",
    headerName: "KYC & AML",
    type: "string",
    width: 150,
    filterOperators,
    renderCell: ({ row: application }) => {
      if (!application.applicants?.length || !application.applicants[0]?.id) {
        return false;
      }
      const hasJointApplicant = application.applicants[1];
      const applicantId = application.applicants[0].id;
      const jointApplicantId = hasJointApplicant
        ? application.applicants[1]?.id
        : false;
      return (
        <Stack>
          <WithDoc
            docRef={`sailorRiskProfiles/${applicantId}`}
            Component={kycAmlStatus}
            fallbackValue={<PendingChip />}
          />
          {jointApplicantId && (
            <WithDoc
              docRef={`sailorRiskProfiles/${jointApplicantId}`}
              Component={kycAmlStatus}
              fallbackValue={<PendingChip />}
            />
          )}
        </Stack>
      );
    },
  },
  {
    field: "idvResult",
    headerName: "ID&V",
    type: "string",
    width: 160,
    filterOperators,
    renderCell: ({ row: application }) => {
      if (!application.applicants?.length || !application.applicants[0]?.id) {
        return false;
      }
      const hasJointApplicant = application.applicants[1];
      const applicantId = application.applicants[0].id;
      const jointApplicantId = hasJointApplicant
        ? application.applicants[1]?.id
        : false;
      return (
        <Stack>
          <LazyActionChip docRef={`sailors/${applicantId}/actions/idv`} />
          {jointApplicantId && (
            <LazyActionChip
              docRef={`sailors/${jointApplicantId}/actions/idv`}
            />
          )}
        </Stack>
      );
    },
  },
  {
    field: "pepResult",
    headerName: "PEP Status",
    type: "string",
    width: 160,
    filterOperators,
    renderCell: ({ row: application }) => {
      if (!application.applicants?.length || !application.applicants[0]?.id) {
        return false;
      }
      const hasJointApplicant = application.applicants[1];
      const applicantId = application.applicants[0].id;
      const jointApplicantId = hasJointApplicant
        ? application.applicants[1]?.id
        : false;
      return (
        <Stack>
          <WithDoc
            docRef={`sailorRiskProfiles/${applicantId}`}
            Component={PepStatusChip}
            fallbackValue={<PendingChip />}
          />
          {jointApplicantId && (
            <WithDoc
              docRef={`sailorRiskProfiles/${jointApplicantId}`}
              Component={PepStatusChip}
              fallbackValue={<PendingChip />}
            />
          )}
        </Stack>
      );
    },
  },
  {
    field: "supportingDocumentation",
    headerName: "Supporting Docs",
    type: "string",
    width: 160,
    filterOperators,
    renderCell: ({ row: application }) => {
      if (!application.applicants?.length || !application.applicants[0]?.id) {
        return false;
      }
      const hasJointApplicant = application.applicants[1];
      const applicantId = application.applicants[0].id;
      const jointApplicantId = hasJointApplicant
        ? application.applicants[1]?.id
        : false;
      return (
        <Stack>
          <LazyActionChip
            docRef={`sailors/${applicantId}/actions/supportingDocumentation`}
          />
          {jointApplicantId && (
            <LazyActionChip
              docRef={`sailors/${jointApplicantId}/actions/supportingDocumentation`}
            />
          )}
        </Stack>
      );
    },
  },
  {
    field: "creditStatus",
    headerName: "Credit Status",
    type: "string",
    width: 150,
    filterOperators,
    renderCell: ({ row }) => {
      return (
        <WithDoc
          docRef={`applications/${row.id}/creditUnderwriting/current`}
          Component={creditUnderwriting}
          fallbackValue={<PendingChip />}
        />
      );
    },
  },
  {
    field: "invoices",
    headerName: "Invoices",
    ...getActionColumnProperties("invoices"),
  },
  {
    field: "insurance",
    headerName: "Insurance",
    ...getActionColumnProperties("insurance"),
  },
  {
    field: "survey",
    headerName: "Survey",
    ...getActionColumnProperties("survey"),
  },
  {
    field: "boatDocumentation",
    headerName: "Boat Documentation",
    ...getActionColumnProperties("boatDocumentation"),
  },
  {
    field: "loanDocs",
    headerName: "Loan Docs",
    ...getActionColumnProperties("loanDocs"),
  },
  dateColumn({
    field: "completionDate",
    headerName: "Completion Date",
    emptyText: "Not Set",
  }),
];
